import { Box, Stack, type SxProps } from "@mui/material";
import { type Theme } from "@mui/material/styles";
import { Button } from "@src/appV2/redesign/components/Button";
import { Text } from "@src/lib/ui-react";
import { type ReactNode } from "react";

const cardSx = (theme: Theme, accent: string) => ({
  borderLeft: `4px solid ${accent}`,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRadius: 1,
  padding: 3,
  backgroundColor: theme.palette.background.paper,
});

export const accentColor = (theme: Theme, isDisabled: boolean) =>
  isDisabled ? theme.palette.error.main : theme.palette.success.main;

export const iconSx = (isDisabled: boolean) => (theme: Theme) => ({
  color: accentColor(theme, isDisabled),
});

export const successText = (theme: Theme) => ({ color: theme.palette.success.main });

export function Caption(props: { color: "error" | "text.secondary"; children: ReactNode }) {
  return (
    <Text variant="caption" color={props.color} sx={{ ml: 4 }}>
      {props.children}
    </Text>
  );
}

/**
 * Shared shell for rate-editor advisory cards (Fill Guarantee, spend-cap over-limit). `action` (the
 * success CTA) is hidden when `isDisabled`; `errorAction` renders even when `isDisabled`, for error-state
 * cards that still offer a recourse (e.g. the over-limit card's "Request increase").
 */
export function RateAdvisoryCard(props: {
  isDisabled: boolean;
  icon: ReactNode;
  title: ReactNode;
  caption: ReactNode;
  testId?: string;
  action?: { label: string; testId: string; sx?: SxProps<Theme>; onClick: () => void };
  errorAction?: {
    label: string;
    testId: string;
    sx?: SxProps<Theme>;
    disabled?: boolean;
    onClick: () => void;
  };
}) {
  return (
    <Box
      data-testid={props.testId}
      sx={(theme) => cardSx(theme, accentColor(theme, props.isDisabled))}
    >
      <Stack spacing={3}>
        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              {props.icon}
              {props.title}
            </Stack>
            {props.caption}
          </Stack>
          {!props.isDisabled && props.action && (
            <Button
              variant="primary"
              size="small"
              data-testid={props.action.testId}
              onClick={props.action.onClick}
              sx={
                [
                  {
                    backgroundColor: "success.main",
                    "&:hover": { backgroundColor: "success.dark" },
                  },
                  props.action.sx ?? false,
                ] as SxProps<Theme>
              }
            >
              {props.action.label}
            </Button>
          )}
        </Stack>
        {/* Error-state CTA (e.g. "Request increase"): full-width button below the caption so it never
            truncates. Uses the design-system `primary` variant (white-on-red) to match the existing
            spend-controls "Request Increase" / "Request approval" buttons. */}
        {props.errorAction && (
          <Button
            fullWidth
            variant="primary"
            disabled={props.errorAction.disabled}
            data-testid={props.errorAction.testId}
            sx={props.errorAction.sx}
            onClick={props.errorAction.onClick}
          >
            {props.errorAction.label}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
